import React from 'react';
import * as Fa6Icons from 'react-icons/fa6';
import * as FaIcons from 'react-icons/fa';

/**
 * Centralized icon mapping utility
 * Maps icon names from JSON (e.g., "faLeaf") to actual React icon components
 * 
 * Usage: 
 *   import { getIcon } from '../utils/iconMap';
 *   {getIcon('faLeaf')}  // Returns <FaLeaf /> component
 *   {getIcon('faLeaf', { size: 24, color: 'red' })}  // With props
 */

// Convert camelCase icon name to PascalCase component name
// e.g., "faLeaf" -> "FaLeaf"
const toPascalCase = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get icon component by name from JSON
 * @param {string} iconName - Icon name from JSON (e.g., "faLeaf", "faMortarPestle")
 * @param {object} props - Optional props to pass to the icon component
 * @returns {React.Element|null} - React icon component or null if not found
 */
export const getIcon = (iconName, props = {}) => {
    if (!iconName) return null;

    const componentName = toPascalCase(iconName);

    // Try FA6 icons first (newer version)
    if (Fa6Icons[componentName]) {
        const IconComponent = Fa6Icons[componentName];
        return <IconComponent {...props} />;
    }

    // Fallback to FA icons (older version)
    if (FaIcons[componentName]) {
        const IconComponent = FaIcons[componentName];
        return <IconComponent {...props} />;
    }

    // Return null if icon not found
    return null;
};

/**
 * Get icon component with a fallback
 * @param {string} iconName - Icon name from JSON
 * @param {string} fallbackName - Fallback icon name if primary not found
 * @param {object} props - Optional props to pass to the icon component
 * @returns {React.Element} - React icon component
 */
export const getIconWithFallback = (iconName, fallbackName = 'faStar', props = {}) => {
    const icon = getIcon(iconName, props);
    if (icon) return icon;

    return getIcon(fallbackName, props) || <Fa6Icons.FaStar {...props} />;
};

export default getIcon;
